import * as React from 'react';
import { cva } from 'class-variance-authority';

import { Backdrop } from './Backdrop';
import { Button } from './Button';
import { IconButton } from './IconButton';
import { WM_FLOATING_SURFACE_CLASSNAME } from './surfaceStyles';
import { Heading, Text } from './Typography';

// Dialog — centred modal. Wraps Backdrop internally.
//
// Closed shape:
//
//   <Dialog
//       open={open}
//       onClose={() => setOpen(false)}
//       title="Edit profile"
//       description="Update your details and save."
//       size="medium"                  // 'small' | 'medium' | 'large' | 'full'
//       closable={true}                // default true
//       actions={[
//           { label: 'Cancel', onClick: cancel, appearance: 'ghost', tone: 'neutral' },
//           { label: 'Save',   onClick: save,   loading: loading }
//       ]}
//   >
//       {body content}
//   </Dialog>
//
// Header chrome (title / description block + Close X) is rendered when
// any of `title`, `description`, or `closable` are present. Footer is
// rendered when `actions` is a non-empty array.
//
// `closable` is the single switch over all casual dismissal paths:
//   true (default) — X in header, Escape, click-outside all fire onClose.
//   false           — X hidden, Escape inert, click-outside inert. The user
//                     must take an explicit action from `actions`. The
//                     primitive does not enforce that `actions` is non-empty
//                     when `closable=false` — that's the caller's
//                     responsibility (and a bug they'll find quickly).
//
// `actions` is a curated subset of the Button API. Each entry maps to
// `<Button {...action}>{action.label}</Button>` internally with the
// supported props below. Props that don't fit the dialog-footer
// convention (size, fullWidth, type, asChild) are intentionally not
// exposed; if a future callsite genuinely needs them, lift them in
// at that point.

const dialogVariants = cva(
    `${WM_FLOATING_SURFACE_CLASSNAME} tw-fixed tw-left-1/2 tw-top-1/2 tw-z-modal tw-flex -tw-translate-x-1/2 -tw-translate-y-1/2 tw-flex-col tw-overflow-hidden tw-rounded-xl`,
    {
        variants: {
            size: {
                small: 'tw-w-full tw-max-w-sm tw-max-h-modal',
                medium: 'tw-w-full tw-max-w-lg tw-max-h-modal',
                large: 'tw-w-full tw-max-w-modal tw-max-h-modal',
                full: 'tw-w-[calc(100vw-2rem)] tw-h-[calc(100vh-2rem)]'
            },
            height: {
                auto: '',
                full: 'tw-h-full'
            }
        },
        defaultVariants: {
            size: 'medium',
            height: 'auto'
        }
    }
);

// The Action props we forward to Button. Curated — Button axes that
// don't apply to dialog-footer actions (size, fullWidth, type, asChild)
// are intentionally not in the list.
const ACTION_BUTTON_PROPS = ['tone', 'appearance', 'loading', 'disabled', 'icon'];

const renderAction = (action, index) => {
    const buttonProps = {};

    ACTION_BUTTON_PROPS.forEach(prop => {
        if (action[prop] !== undefined) {
            buttonProps[prop] = action[prop];
        }
    });

    return (
        <Button key={index} {...buttonProps} onClick={action.onClick}>
            {action.label}
        </Button>
    );
};

function Dialog({
    open,
    onClose,
    title,
    description,
    closable = true,
    actions,
    size = 'medium',
    height = 'auto',
    children
}) {
    if (!open) {
        return null;
    }

    const hasHeader = title || description || closable;
    const hasFooter = actions && actions.length > 0;
    const backdropOnClose = closable ? onClose : undefined;

    return (
        <Backdrop full onClose={backdropOnClose}>
            <div role="dialog" aria-modal="true" className={dialogVariants({ size, height })}>
                {hasHeader ? (
                    <div className="tw-flex tw-items-center tw-justify-between tw-gap-4 tw-p-6 tw-pb-2">
                        <div className="tw-flex tw-flex-col tw-gap-1">
                            {title ? <Heading as="h2" variant="title">{title}</Heading> : null}
                            {description ? <Text tone="muted" variant="body">{description}</Text> : null}
                        </div>
                        {/* Close button is always rendered when the header exists so
                            the layout doesn't shift when `closable` flips at runtime
                            (e.g. mid-action loading). When `closable={false}` the
                            button paints disabled — visible cue that dismissal is
                            currently locked while still preserving the box width. */}
                        <IconButton
                            aria-label="Close"
                            disabled={!closable}
                            icon="close"
                            onClick={closable ? onClose : undefined}
                        />
                    </div>
                ) : null}
                <div className="tw-flex-1 tw-overflow-y-auto tw-px-6 tw-py-2">
                    {children}
                </div>
                {hasFooter ? (
                    <div className="tw-flex tw-flex-col-reverse tw-gap-2 tw-p-6 tw-pt-2 sm:tw-flex-row sm:tw-justify-end">
                        {actions.map(renderAction)}
                    </div>
                ) : null}
            </div>
        </Backdrop>
    );
}

export { Dialog };
